import React from "react";
import { Settings } from "lucide-react";

const FontStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700&family=DM+Mono:wght@500&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'DM Sans', sans-serif; background: #F8F8F8; }
    input, select { font-family: 'DM Sans', sans-serif; }
    .rx-scroll::-webkit-scrollbar { width: 4px; }
    .rx-scroll::-webkit-scrollbar-thumb { background: #CCCCCC; border-radius: 2px; }
    .rx-scroll::-webkit-scrollbar-track { background: transparent; }
    @keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
    @keyframes pulse { 0%,100%{opacity:1;} 50%{opacity:0.3;} }
    .fade-up { animation: fadeUp 0.4s ease forwards; }
    .live { animation: pulse 2s ease-in-out infinite; }
  `}</style>
);

const DEFAULT = {
  company:  { name: "", crm: "HubSpot", fiscalYear: new Date().getFullYear() },
  revenue:  { target: "", configured: false, monthlyTargets: {
    jan:"", feb:"", mar:"", apr:"", may:"", jun:"",
    jul:"", aug:"", sep:"", oct:"", nov:"", dec:""
  }},
  segments: { configured: false, list: [
    { name: "New Logo",    goal: "", color: "#CC1A1A" },
    { name: "Upsell",     goal: "", color: "#D97706" },
    { name: "Cross-sell", goal: "", color: "#7C3AED" },
  ], attribution: { mode: "rep", property: "", valueMap: {} }},
  stages: { configured: false, list: [
    { label: "Prospecting", id: "" }, { label: "Discovery",   id: "" },
    { label: "Evaluation",  id: "" }, { label: "Proposal",    id: "" },
    { label: "Negotiation", id: "" }, { label: "Closed Won",  id: "" },
    { label: "Closed Lost", id: "" },
  ]},
  targets: { configured: false, winRate: "", avgDealSize: "", salesCycle: "", nrr: "", bdrCount: "", aeCount: "", bySegment: {}, byTeam: {}, dealTypes: {} },
  team:    { configured: false, totalQuota: "", reps: [{ name: "", segment: "", quota: "", hubspotOwnerId: "", startDate: "", role: "" }] },
  hubspot: { configured: false, token: "", portalId: "" },
  fieldMap: {
    configured: false,

    // ── GROUP 1: Deal Core (required — drives all revenue math) ──
    dealname:         "dealname",
    dealstage:        "dealstage",
    amount:           "amount",
    closedate:        "closedate",
    createdate:       "createdate",
    owner:            "hubspot_owner_id",

    // ── GROUP 2: Deal Enrichment (forecasting + segmentation) ──
    forecastCategory: "hs_forecast_category",
    dealtype:         "dealtype",
    probability:      "hs_deal_stage_probability",
    pipeline:         "pipeline",

    // ── GROUP 3: CRM Health signals ──
    lastActivity:     "notes_last_activity_date",
    lastModified:     "hs_lastmodifieddate",
    nextStep:         "hs_next_step",
    numContacts:      "num_associated_contacts",

    // ── GROUP 4: Account Coverage (company object) ──
    acctOwner:        "hubspot_owner_id",
    acctLastActivity: "notes_last_activity_date",
    acctLastContacted:"notes_last_contacted",
    acctOpenDeals:    "hs_num_open_deals",
    acctLifecycle:    "lifecyclestage",
    acctArr:          "annualrevenue",
  },
  nrr: {
    configured: false,
    segments: [
      { name: "Enterprise", startingArr: "", churnPct: "", expansionRate: "" },
      { name: "Mid-Market", startingArr: "", churnPct: "", expansionRate: "" },
      { name: "SMB",        startingArr: "", churnPct: "", expansionRate: "" },
    ],
    cohorts: [
      { year: new Date().getFullYear()-3, startingLogos: "", yr1: "", yr2: "", yr3: "" },
      { year: new Date().getFullYear()-2, startingLogos: "", yr1: "", yr2: "", yr3: "" },
      { year: new Date().getFullYear()-1, startingLogos: "", yr1: "", yr2: "", yr3: "" },
    ],
  },
  funnel: {
    configured: false,
    monthlyLeads: "", mqls: "", sqls: "",
    sources: [
      { name: "Inbound / Website",   volume: "", mqlRate: "", avgDeal: "" },
      { name: "Outbound / BDR",      volume: "", mqlRate: "", avgDeal: "" },
      { name: "Paid Demand Gen",     volume: "", mqlRate: "", avgDeal: "" },
      { name: "Partner / Referral",  volume: "", mqlRate: "", avgDeal: "" },
      { name: "Events",              volume: "", mqlRate: "", avgDeal: "" },
      { name: "Product-Led / Trial", volume: "", mqlRate: "", avgDeal: "" },
    ],
  },
  por: {
    configured: false,
    priorities: [
      { n:"01", area:"", why:"", impact:"CRITICAL", timeline:"0–30 days",  owner:"", status:"Not Started" },
      { n:"02", area:"", why:"", impact:"HIGH",     timeline:"0–30 days",  owner:"", status:"Not Started" },
      { n:"03", area:"", why:"", impact:"HIGH",     timeline:"30–60 days", owner:"", status:"Not Started" },
      { n:"04", area:"", why:"", impact:"MED",      timeline:"30–90 days", owner:"", status:"Not Started" },
      { n:"05", area:"", why:"", impact:"MED",      timeline:"60–90 days", owner:"", status:"Not Started" },
    ],
  },
};

const deepMerge = (base, override) => {
  const result = { ...base };
  for (const key of Object.keys(override || {})) {
    if (override[key] && typeof override[key] === "object" && !Array.isArray(override[key]) && base[key] && typeof base[key] === "object" && !Array.isArray(base[key])) {
      result[key] = deepMerge(base[key], override[key]);
    } else {
      result[key] = override[key];
    }
  }
  return result;
};
// ── Module-level field resolver (used outside calcMetrics) ──
// resolveF — maps every logical field name to the actual HubSpot property name.
// Always call this instead of reading fieldMap directly.
const resolveF = (settings) => {
  const fm = settings?.fieldMap || {};
  return {
    // Core deal fields
    dealname:         fm.dealname         || "dealname",
    dealstage:        fm.dealstage        || "dealstage",
    amount:           fm.amount           || "amount",
    closedate:        fm.closedate        || "closedate",
    createdate:       fm.createdate       || "createdate",
    owner:            fm.owner            || "hubspot_owner_id",
    // Enrichment
    forecastCategory: fm.forecastCategory || "hs_forecast_category",
    dealtype:         fm.dealtype         || "dealtype",
    probability:      fm.probability      || "hs_deal_stage_probability",
    pipeline:         fm.pipeline         || "pipeline",
    // CRM Health
    lastActivity:     fm.lastActivity     || "notes_last_activity_date",
    lastModified:     fm.lastModified     || "hs_lastmodifieddate",
    nextStep:         fm.nextStep         || "hs_next_step",
    numContacts:      fm.numContacts      || "num_associated_contacts",
  };
};

// dealProps — the full list of deal properties to request from HubSpot,
// derived from the current field mapping. Pass this to the deals endpoint.
const dealProps = (settings) => {
  const F = resolveF(settings);
  return [...new Set(Object.values(F))].join(",");
};

const loadS = () => { try { const s = localStorage.getItem("rx_settings"); return s ? deepMerge(DEFAULT, JSON.parse(s)) : DEFAULT; } catch { return DEFAULT; } };
const saveS = (s) => localStorage.setItem("rx_settings", JSON.stringify(s));

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("Screen crash:", error, info); }
  reset() { this.setState({ hasError: false, error: null }); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{padding:40,textAlign:"center"}}>
          <div style={{fontSize:32,marginBottom:12}}>⚠️</div>
          <div style={{fontSize:16,fontWeight:700,color:"#111111",marginBottom:8}}>Something went wrong on this screen</div>
          <div style={{fontSize:12,color:"#555555",marginBottom:20,fontFamily:"'DM Mono',monospace",maxWidth:500,margin:"0 auto 20px"}}>
            {this.state.error?.message || "Unknown error"}
          </div>
          <button onClick={()=>this.reset()}
            style={{padding:"9px 24px",background:"#CC1A1A",color:"#F8F8F8",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer"}}>
            Try Again
          </button>
          <div style={{fontSize:11,color:"#555555",marginTop:10}}>Your settings are safe — they were auto-saved.</div>
        </div>
      );
    }
    return this.props.children;
  }
}

const fmtM = (n) => { const v=+n; if(!v) return "—"; if(v>=1000000) return `$${(v/1000000).toFixed(1)}M`; if(v>=1000) return `$${(v/1000).toFixed(0)}K`; return `$${v.toLocaleString()}`; };
const fmtX = (n) => n ? `$${Number(n).toLocaleString()}` : "—";
const dealUrl = (portalId, dealId) => (portalId && dealId)
  ? `https://app.hubspot.com/contacts/${portalId}/deal/${dealId}` : null;


const PROXY = "/api/hubspot";

// Strip deals to only fields RevXcel uses — keeps localStorage well under 5MB limit
// KEEP_FIELDS — default set of HubSpot property names to retain when trimming deals.
// At runtime, App.js replaces this with dealProps(settings) values.
const KEEP_FIELDS = [
  "dealname","amount","dealstage","closedate","createdate","dealtype",
  "hubspot_owner_id","hs_deal_stage_probability","pipeline",
  "hs_forecast_category","notes_last_activity_date","hs_lastmodifieddate",
  "hs_next_step","num_associated_contacts",
];
// slimDeal retains only the mapped fields (avoids bloating localStorage cache)
const slimDeal = (d, keepFields) => ({
  id: d.id,
  properties: (keepFields || KEEP_FIELDS).reduce((acc, k) => {
    if (d.properties?.[k] !== undefined) acc[k] = d.properties[k];
    return acc;
  }, {}),
});
const slimDeals = (arr, keepFields) => (arr || []).map(d => slimDeal(d, keepFields));

const Badge = ({ status }) => {
  const map = { critical:["rgba(220,38,38,0.10)","#DC2626","CRITICAL"], high:["rgba(234,123,30,0.12)","#C2610C","HIGH"], medium:["rgba(109,40,217,0.10)","#7C3AED","MED"], good:["rgba(22,163,74,0.12)","#16A34A","ON TRACK"], watch:["rgba(234,123,30,0.12)","#C2610C","AT RISK"], risk:["rgba(220,38,38,0.10)","#DC2626","HIGH RISK"] };
  const [bg,fg,label] = map[status]||map.medium;
  return <span style={{background:bg,color:fg,fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:4,letterSpacing:"0.05em",whiteSpace:"nowrap"}}>{label}</span>;
};

const Card = ({children,style={}}) => (
  <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.08)",borderRadius:10,padding:18,...style}}>{children}</div>
);

const SH = ({title,sub}) => (
  <div style={{marginBottom:14}}>
    <div style={{fontSize:14,fontWeight:700,color:"#111111"}}>{title}</div>
    {sub && <div style={{fontSize:11,color:"#555555",marginTop:2}}>{sub}</div>}
  </div>
);

const tt = { contentStyle:{background:"#F0F0F0",border:"1px solid rgba(204,26,26,0.10)",borderRadius:8,fontSize:11,color:"#999999"} };

const Inp = ({label,value,onChange,placeholder,type="text",hint,id}) => {
  const _id = id || (label ? "inp-"+label.toLowerCase().replace(/[^a-z0-9]/g,"-") : undefined);
  return (
    <div style={{marginBottom:14}}>
      {label && <div style={{fontSize:11,fontWeight:600,color:"#555555",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</div>}
      <input id={_id} name={_id} type={type} value={value ?? ""} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        style={{width:"100%",background:"#F0F0F0",border:"1px solid rgba(204,26,26,0.10)",borderRadius:7,padding:"9px 12px",color:"#111111",fontSize:13,outline:"none"}}/>
      {hint && <div style={{fontSize:10,color:"#555555",marginTop:4}}>{hint}</div>}
    </div>
  );
};

const SetupBanner = ({section,onGo}) => (
  <div style={{background:"linear-gradient(135deg,#1a0f00,#0f0800)",border:"1px solid rgba(217,119,6,0.12)",borderRadius:12,padding:"32px 28px",textAlign:"center"}}>
    <div style={{fontSize:36,marginBottom:12}}>⚙️</div>
    <div style={{fontSize:16,fontWeight:700,color:"#111111",marginBottom:8}}>{section} Not Configured</div>
    <div style={{fontSize:13,color:"#555555",marginBottom:20,lineHeight:1.6}}>Complete the setup in Settings to see your live data here.<br/>Takes about 2 minutes per section.</div>
    <button onClick={onGo} style={{background:"#D97706",color:"#F8F8F8",border:"none",borderRadius:8,padding:"10px 24px",fontSize:13,fontWeight:700,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:8}}>
      <Settings size={14}/> Go to Settings
    </button>
  </div>
);


export {
  FontStyle, DEFAULT, deepMerge, resolveF, dealProps, loadS, saveS,
  fmtM, fmtX, dealUrl,
  PROXY, KEEP_FIELDS, slimDeal, slimDeals,
  ErrorBoundary,
  Badge, Card, SH, tt, Inp, SetupBanner,
};
